const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Formats how long ago something happened, for labels such as "Updated 5 minutes ago".
 *
 * - Under a minute is "just now"; below that granularity the exact number is noise.
 * - Then minutes, hours and days, each pluralised.
 * - A timestamp in the future is clamped to "just now" rather than reported as negative,
 *   since a clock skew between the browser and the backend is not worth surfacing.
 *
 * Returns an **empty string** when there is no usable timestamp — missing, or a value the
 * browser cannot parse. Callers decide how to render that; they must not fall through to a
 * relative label, which would claim a freshness the data never established.
 */
export function formatRelativeTimeLabel(value: Date | string | null | undefined): string {
  if (!value) {
    return '';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const diffMs = Math.max(0, Date.now() - date.getTime());

  const days = Math.floor(diffMs / DAY_MS);
  if (days > 0) {
    return formatUnitsAgo(days, 'day');
  }

  const hours = Math.floor(diffMs / HOUR_MS);
  if (hours > 0) {
    return formatUnitsAgo(hours, 'hour');
  }

  const minutes = Math.floor(diffMs / MINUTE_MS);
  if (minutes > 0) {
    return formatUnitsAgo(minutes, 'minute');
  }

  return 'just now';
}

function formatUnitsAgo(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? '' : 's'} ago`;
}
